import NIM from 'nim-web-sdk-ng/dist/v2/NIM_BROWSER_SDK'
import { MESSAGE_STATUS, MESSAGE_TYPE } from './enums.js'

const STORAGE_KEY = 'yunxinLoginInfo'
const AES_TEXT_PREFIX = 'aesmsg:'
const USER_STATUS_TYPE_LOGIN = 1
const USER_STATUS_TYPE_LOGOUT = 2
const USER_STATUS_TYPE_DISCONNECT = 3
const LOGIN_CLIENT_TYPE_ANDROID = 1
const LOGIN_CLIENT_TYPE_IOS = 2
const LOGIN_CLIENT_TYPE_PC = 4
const LOGIN_CLIENT_TYPE_WEB = 16
const LOGIN_CLIENT_TYPE_MAC_OS = 64
const LOGIN_CLIENT_TYPE_HARMONY_OS = 65

function getAppKey() {
	return process.env.VUE_APP_YUNXIN_APP_KEY || ''
}

function getIsOverseas() {
	return String(process.env.VUE_APP_YUNXIN_IS_OVERSEAS || 'false') === 'true'
}

function buildNimInitOptions() {
	const nimOptions = {
		appkey: getAppKey(),
		needReconnect: true,
		debugLevel: process.env.NODE_ENV === 'production' ? 'off' : 'debug',
		apiVersion: 'v2',
		enableV2CloudConversation: true
	}
	const otherOptions = {
		V2NIMLoginServiceConfig: {
			lbsUrls: ['https://lbs.netease.im/lbs/webconf.jsp'],
			linkUrl: 'weblink.netease.im',
			isFixedDeviceId: true
		},
		V2NIMFriendServiceConfig: {
			enableServerV2FriendAddApplication: true
		}
	}

	if (getIsOverseas()) {
		otherOptions.V2NIMLoginServiceConfig = {
			lbsUrls: ['https://lbs.netease.im/lbs/webconf'],
			linkUrl: 'weblink01-sg.netease.im:443',
			isFixedDeviceId: true
		}
		otherOptions.privateConf = {
			nos_uploader_web: 'https://upload.chatnos.com'
		}
	}

	return {
		nimOptions,
		otherOptions
	}
}

function normalizeLoginInfo(loginInfo = {}) {
	const account = loginInfo.imAccid || loginInfo.account || loginInfo.accid || ''
	const token = loginInfo.imToken || loginInfo.token || ''
	return {
		...loginInfo,
		account,
		token,
		imAccid: account,
		imToken: token
	}
}

function parseJson(value, fallback = {}) {
	if (!value) {
		return fallback
	}
	if (typeof value === 'object') {
		return value
	}
	try {
		return JSON.parse(value)
	} catch (error) {
		return fallback
	}
}

function getCryptoApi() {
	if (typeof window !== 'undefined' && window.crypto) {
		return window.crypto
	}
	if (typeof self !== 'undefined' && self.crypto) {
		return self.crypto
	}
	return undefined
}

function normalizeAesSeed(senderId) {
	const seed = (senderId || '').slice(1, 6).split('').sort().join('')
	if (!seed) {
		return '0'.repeat(16)
	}
	return seed.repeat(Math.ceil(16 / seed.length)).slice(0, 16)
}

function isEncryptedMessageText(text) {
	return typeof text === 'string' && text.startsWith(AES_TEXT_PREFIX)
}

function base64ToBytes(value) {
	const binary = atob(value)
	const bytes = new Uint8Array(binary.length)
	for (let i = 0; i < binary.length; i += 1) {
		bytes[i] = binary.charCodeAt(i)
	}
	return bytes
}

function decodeUtf8(buffer) {
	return new TextDecoder('utf-8', { fatal: true }).decode(buffer)
}

function isReadablePlainText(value) {
	if (!value) {
		return false
	}
	const invalidCount = (value.match(/\uFFFD/g) || []).length
	return invalidCount === 0 && !Array.from(value).some(char => {
		const code = char.charCodeAt(0)
		return code < 32 && code !== 9 && code !== 10 && code !== 13
	})
}

function utf8ToBytes(value) {
	return new TextEncoder().encode(value)
}

function md5Hex(value) {
	function rotateLeft(num, count) {
		return (num << count) | (num >>> (32 - count))
	}
	function addUnsigned(a, b) {
		return (a + b) >>> 0
	}
	function cmn(q, a, b, x, s, t) {
		return addUnsigned(rotateLeft(addUnsigned(addUnsigned(a, q), addUnsigned(x, t)), s), b)
	}
	function ff(a, b, c, d, x, s, t) {
		return cmn((b & c) | ((~b) & d), a, b, x, s, t)
	}
	function gg(a, b, c, d, x, s, t) {
		return cmn((b & d) | (c & (~d)), a, b, x, s, t)
	}
	function hh(a, b, c, d, x, s, t) {
		return cmn(b ^ c ^ d, a, b, x, s, t)
	}
	function ii(a, b, c, d, x, s, t) {
		return cmn(c ^ (b | (~d)), a, b, x, s, t)
	}
	function toWords(input) {
		const bytes = utf8ToBytes(input)
		const wordsLength = (((bytes.length + 8) >> 6) + 1) * 16
		const words = new Array(wordsLength).fill(0)
		for (let i = 0; i < bytes.length; i += 1) {
			words[i >> 2] |= bytes[i] << ((i % 4) * 8)
		}
		words[bytes.length >> 2] |= 0x80 << ((bytes.length % 4) * 8)
		words[wordsLength - 2] = bytes.length * 8
		return words
	}
	function wordToHex(word) {
		let result = ''
		for (let i = 0; i < 4; i += 1) {
			result += ((word >>> (i * 8)) & 255).toString(16).padStart(2, '0')
		}
		return result
	}
	const words = toWords(value)
	let a = 0x67452301
	let b = 0xefcdab89
	let c = 0x98badcfe
	let d = 0x10325476
	for (let i = 0; i < words.length; i += 16) {
		const aa = a
		const bb = b
		const cc = c
		const dd = d
		a = ff(a, b, c, d, words[i + 0], 7, 0xd76aa478); d = ff(d, a, b, c, words[i + 1], 12, 0xe8c7b756); c = ff(c, d, a, b, words[i + 2], 17, 0x242070db); b = ff(b, c, d, a, words[i + 3], 22, 0xc1bdceee)
		a = ff(a, b, c, d, words[i + 4], 7, 0xf57c0faf); d = ff(d, a, b, c, words[i + 5], 12, 0x4787c62a); c = ff(c, d, a, b, words[i + 6], 17, 0xa8304613); b = ff(b, c, d, a, words[i + 7], 22, 0xfd469501)
		a = ff(a, b, c, d, words[i + 8], 7, 0x698098d8); d = ff(d, a, b, c, words[i + 9], 12, 0x8b44f7af); c = ff(c, d, a, b, words[i + 10], 17, 0xffff5bb1); b = ff(b, c, d, a, words[i + 11], 22, 0x895cd7be)
		a = ff(a, b, c, d, words[i + 12], 7, 0x6b901122); d = ff(d, a, b, c, words[i + 13], 12, 0xfd987193); c = ff(c, d, a, b, words[i + 14], 17, 0xa679438e); b = ff(b, c, d, a, words[i + 15], 22, 0x49b40821)
		a = gg(a, b, c, d, words[i + 1], 5, 0xf61e2562); d = gg(d, a, b, c, words[i + 6], 9, 0xc040b340); c = gg(c, d, a, b, words[i + 11], 14, 0x265e5a51); b = gg(b, c, d, a, words[i + 0], 20, 0xe9b6c7aa)
		a = gg(a, b, c, d, words[i + 5], 5, 0xd62f105d); d = gg(d, a, b, c, words[i + 10], 9, 0x02441453); c = gg(c, d, a, b, words[i + 15], 14, 0xd8a1e681); b = gg(b, c, d, a, words[i + 4], 20, 0xe7d3fbc8)
		a = gg(a, b, c, d, words[i + 9], 5, 0x21e1cde6); d = gg(d, a, b, c, words[i + 14], 9, 0xc33707d6); c = gg(c, d, a, b, words[i + 3], 14, 0xf4d50d87); b = gg(b, c, d, a, words[i + 8], 20, 0x455a14ed)
		a = gg(a, b, c, d, words[i + 13], 5, 0xa9e3e905); d = gg(d, a, b, c, words[i + 2], 9, 0xfcefa3f8); c = gg(c, d, a, b, words[i + 7], 14, 0x676f02d9); b = gg(b, c, d, a, words[i + 12], 20, 0x8d2a4c8a)
		a = hh(a, b, c, d, words[i + 5], 4, 0xfffa3942); d = hh(d, a, b, c, words[i + 8], 11, 0x8771f681); c = hh(c, d, a, b, words[i + 11], 16, 0x6d9d6122); b = hh(b, c, d, a, words[i + 14], 23, 0xfde5380c)
		a = hh(a, b, c, d, words[i + 1], 4, 0xa4beea44); d = hh(d, a, b, c, words[i + 4], 11, 0x4bdecfa9); c = hh(c, d, a, b, words[i + 7], 16, 0xf6bb4b60); b = hh(b, c, d, a, words[i + 10], 23, 0xbebfbc70)
		a = hh(a, b, c, d, words[i + 13], 4, 0x289b7ec6); d = hh(d, a, b, c, words[i + 0], 11, 0xeaa127fa); c = hh(c, d, a, b, words[i + 3], 16, 0xd4ef3085); b = hh(b, c, d, a, words[i + 6], 23, 0x04881d05)
		a = hh(a, b, c, d, words[i + 9], 4, 0xd9d4d039); d = hh(d, a, b, c, words[i + 12], 11, 0xe6db99e5); c = hh(c, d, a, b, words[i + 15], 16, 0x1fa27cf8); b = hh(b, c, d, a, words[i + 2], 23, 0xc4ac5665)
		a = ii(a, b, c, d, words[i + 0], 6, 0xf4292244); d = ii(d, a, b, c, words[i + 7], 10, 0x432aff97); c = ii(c, d, a, b, words[i + 14], 15, 0xab9423a7); b = ii(b, c, d, a, words[i + 5], 21, 0xfc93a039)
		a = ii(a, b, c, d, words[i + 12], 6, 0x655b59c3); d = ii(d, a, b, c, words[i + 3], 10, 0x8f0ccc92); c = ii(c, d, a, b, words[i + 10], 15, 0xffeff47d); b = ii(b, c, d, a, words[i + 1], 21, 0x85845dd1)
		a = ii(a, b, c, d, words[i + 8], 6, 0x6fa87e4f); d = ii(d, a, b, c, words[i + 15], 10, 0xfe2ce6e0); c = ii(c, d, a, b, words[i + 6], 15, 0xa3014314); b = ii(b, c, d, a, words[i + 13], 21, 0x4e0811a1)
		a = ii(a, b, c, d, words[i + 4], 6, 0xf7537e82); d = ii(d, a, b, c, words[i + 11], 10, 0xbd3af235); c = ii(c, d, a, b, words[i + 2], 15, 0x2ad7d2bb); b = ii(b, c, d, a, words[i + 9], 21, 0xeb86d391)
		a = addUnsigned(a, aa); b = addUnsigned(b, bb); c = addUnsigned(c, cc); d = addUnsigned(d, dd)
	}
	return `${wordToHex(a)}${wordToHex(b)}${wordToHex(c)}${wordToHex(d)}`
}

async function importAesKey(senderId, algorithm, usages = ['decrypt']) {
	const rawKey = utf8ToBytes(normalizeAesSeed(senderId))
	return getCryptoApi().subtle.importKey('raw', rawKey, algorithm, false, usages)
}

async function decryptMessageTextBySender(senderId, cipherText) {
	const cryptoApi = getCryptoApi()
	const payload = base64ToBytes(cipherText)
	try {
		const iv = payload.slice(0, 12)
		const cipher = payload.slice(12)
		const key = await importAesKey(senderId, 'AES-GCM')
		const decrypted = await cryptoApi.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher)
		const plainText = decodeUtf8(decrypted)
		return isReadablePlainText(plainText) ? plainText : ''
	} catch (error) {
		try {
			const normalizedKey = normalizeAesSeed(senderId)
			const iv = utf8ToBytes(md5Hex(normalizedKey).slice(0, 16))
			const key = await importAesKey(senderId, { name: 'AES-CBC' })
			const decrypted = await cryptoApi.subtle.decrypt({ name: 'AES-CBC', iv }, key, payload)
			const plainText = decodeUtf8(decrypted)
			return isReadablePlainText(plainText) ? plainText : ''
		} catch (fallbackError) {
			return ''
		}
	}
}

async function decryptMessageText(senderIds, text) {
	if (!text || !isEncryptedMessageText(text)) {
		return text || ''
	}
	const cryptoApi = getCryptoApi()
	if (!cryptoApi?.subtle || typeof TextDecoder === 'undefined' || typeof TextEncoder === 'undefined') {
		return text
	}
	const cipherText = text.slice(AES_TEXT_PREFIX.length)
	const candidates = Array.isArray(senderIds) ? senderIds : [senderIds]
	for (const senderId of [...new Set(candidates.filter(Boolean).map(item => String(item)))]) {
		const plainText = await decryptMessageTextBySender(senderId, cipherText)
		if (plainText) {
			return plainText
		}
	}
	console.warn('[YunxinIM] 文本解密失败，保留原文')
	return text
}

function getMessageSenderId(message = {}) {
	return message.senderId || message.from || message.fromAccountId || message.accountId || message.sendId ||
		message.messageRefer?.senderId || message.messageRefer?.fromAccountId || ''
}

function getMessageSenderCandidates(message = {}, fallbackId = '') {
	return [
		message.senderId,
		message.from,
		message.fromAccountId,
		message.accountId,
		message.sendId,
		message.messageRefer?.senderId,
		message.messageRefer?.fromAccountId,
		fallbackId,
		message.conversationId ? String(message.conversationId).split('|').pop() : ''
	]
}

function resolveSdkMessageType(message = {}) {
	const sdkType = message.messageType
	if (typeof sdkType === 'number') {
		return sdkType
	}
	const typeMap = {
		MESSAGE_TYPE_TEXT: 0,
		V2NIM_MESSAGE_TYPE_TEXT: 0,
		MESSAGE_TYPE_IMAGE: 1,
		V2NIM_MESSAGE_TYPE_IMAGE: 1,
		MESSAGE_TYPE_AUDIO: 2,
		V2NIM_MESSAGE_TYPE_AUDIO: 2,
		MESSAGE_TYPE_VIDEO: 3,
		V2NIM_MESSAGE_TYPE_VIDEO: 3,
		MESSAGE_TYPE_NOTIFICATION: 5,
		V2NIM_MESSAGE_TYPE_NOTIFICATION: 5,
		MESSAGE_TYPE_FILE: 6,
		V2NIM_MESSAGE_TYPE_FILE: 6,
		MESSAGE_TYPE_TIPS: 10,
		V2NIM_MESSAGE_TYPE_TIPS: 10,
		MESSAGE_TYPE_CUSTOM: 100,
		V2NIM_MESSAGE_TYPE_CUSTOM: 100
	}
	return typeMap[sdkType] ?? null
}

function resolveLegacyMessageType(message = {}) {
	const sdkType = resolveSdkMessageType(message)
	if (sdkType === 100) {
		const customAttachment = getCustomAttachmentData(message)
		const customType = customAttachment.type ?? message.attachment?.type ?? message.attach?.type ?? message.type
		if (customType === MESSAGE_TYPE.USER_CARD || customType === MESSAGE_TYPE.GROUP_CARD) {
			return customType
		}
	}
	switch (sdkType) {
		case 0:
			return MESSAGE_TYPE.TEXT
		case 1:
			return MESSAGE_TYPE.IMAGE
		case 2:
			return MESSAGE_TYPE.AUDIO
		case 3:
			return MESSAGE_TYPE.VIDEO
		case 5:
		case 10:
			return MESSAGE_TYPE.TIP_TEXT
		case 6:
			return MESSAGE_TYPE.FILE
		default:
			return message.type ?? MESSAGE_TYPE.TEXT
	}
}

function getCustomAttachmentData(message = {}) {
	const attachment = message.attachment || message.attach || {}
	const raw = attachment.raw || message.rawAttachment || ''
	return parseJson(raw, attachment)
}

function getAttachmentContent(message = {}, legacyType) {
	const attachment = message.attachment || message.attach || {}
	if (legacyType === MESSAGE_TYPE.USER_CARD || legacyType === MESSAGE_TYPE.GROUP_CARD) {
		const customAttachment = getCustomAttachmentData(message)
		return JSON.stringify(customAttachment.data || customAttachment.cardData || customAttachment)
	}
	if (legacyType === MESSAGE_TYPE.IMAGE) {
		return JSON.stringify({
			originUrl: attachment.url || attachment.originUrl || attachment.path || '',
			thumbUrl: attachment.thumbUrl || attachment.url || attachment.path || '',
			width: Number(attachment.width || 0),
			height: Number(attachment.height || 0)
		})
	}
	if (legacyType === MESSAGE_TYPE.FILE) {
		return JSON.stringify({
			name: attachment.name || attachment.fileName || '',
			size: Number(attachment.size || 0),
			url: attachment.url || attachment.path || ''
		})
	}
	if (legacyType === MESSAGE_TYPE.AUDIO) {
		return JSON.stringify({
			duration: Number(attachment.duration || attachment.dur || 0),
			url: attachment.url || attachment.path || '',
			size: Number(attachment.size || 0)
		})
	}
	if (legacyType === MESSAGE_TYPE.VIDEO) {
		return JSON.stringify({
			coverUrl: attachment.coverUrl || attachment.thumbUrl || '',
			videoUrl: attachment.url || attachment.videoUrl || attachment.path || '',
			duration: Number(attachment.duration || 0),
			width: Number(attachment.width || 0),
			height: Number(attachment.height || 0),
			size: Number(attachment.size || 0)
		})
	}
	return message.textContent?.content ?? message.text ?? message.content ?? ''
}

function getTargetIdFromConversation(message = {}) {
	if (message.receiverId || message.to || message.recvId) {
		return message.receiverId || message.to || message.recvId
	}
	const conversationId = String(message.conversationId || '')
	const parts = conversationId.split('|')
	return parts[parts.length - 1] || ''
}

function normalizeSdkMessageToLegacy(message = {}) {
	const legacyType = resolveLegacyMessageType(message)
	const extension = parseJson(message.serverExtension || message.localExtension, {})
	const conversationType = Number(message.conversationType || 0)
	const isGroup = conversationType === 2 || String(message.conversationId || '').includes('team')
	const senderId = getMessageSenderId(message)
	const receiverId = getTargetIdFromConversation(message)
	const id = message.messageServerId || message.messageClientId || message.id || `${senderId}_${message.createTime || Date.now()}`
	const legacyMessage = {
		id,
		tmpId: message.messageClientId || id,
		conversationId: message.conversationId || '',
		sendId: senderId,
		recvId: receiverId,
		groupId: isGroup ? receiverId : undefined,
		content: getAttachmentContent(message, legacyType),
		type: legacyType,
		status: MESSAGE_STATUS.DELIVERED,
		sendTime: Number(message.createTime || message.sendTime || message.time || Date.now()),
		sendNickName: message.senderNickName || message.fromNick || message.fromNickname || '',
		atUserIds: extension.atUserIds || message.atUserIds || [],
		quoteMessage: extension.quoteMessage || message.quoteMessage || null,
		receipt: !!(extension.teamReceiptMessage || message.teamReceiptEnabled || message.messageConfig?.readReceiptEnabled),
		attachment: message.attachment || message.attach || {},
		sourceMessage: message,
		chatType: isGroup ? 'GROUP' : 'PRIVATE'
	}
	if (!legacyMessage.groupId) {
		delete legacyMessage.groupId
	}
	return legacyMessage
}

function normalizeSdkRevokeNotificationToLegacy(notification = {}, currentAccount = '') {
	const refer = notification.messageRefer || notification
	const conversationType = Number(refer.conversationType || 0)
	const isGroup = conversationType === 2 || String(refer.conversationId || '').includes('team')
	const targetId = refer.receiverId || getTargetIdFromConversation(refer)
	const revokeAccountId = notification.revokeAccountId || currentAccount || refer.senderId || ''
	return {
		id: `${refer.messageServerId || refer.messageClientId || Date.now()}_recall`,
		conversationId: refer.conversationId || '',
		sendId: revokeAccountId,
		recvId: isGroup ? undefined : targetId,
		groupId: isGroup ? targetId : undefined,
		content: refer.messageServerId || refer.messageClientId || notification.id || '',
		type: MESSAGE_TYPE.RECALL,
		status: MESSAGE_STATUS.DELIVERED,
		sendTime: Date.now(),
		selfSend: String(revokeAccountId) === String(currentAccount),
		chatType: isGroup ? 'GROUP' : 'PRIVATE',
		sourceNotification: notification
	}
}

function applyOutgoingMessageExtension(message, options = {}) {
	const sendOptions = { ...options }
	if (sendOptions.serverExtension) {
		message.serverExtension = typeof sendOptions.serverExtension === 'string'
			? sendOptions.serverExtension
			: JSON.stringify(sendOptions.serverExtension)
		delete sendOptions.serverExtension
	}
	return sendOptions
}


async function normalizeSdkMessageToLegacyAsync(message = {}) {
	const legacyType = resolveLegacyMessageType(message)
	if (legacyType !== MESSAGE_TYPE.TEXT) {
		return normalizeSdkMessageToLegacy(message)
	}
	const text = message.textContent?.content ?? message.text ?? message.content ?? ''
	if (!isEncryptedMessageText(text)) {
		return normalizeSdkMessageToLegacy(message)
	}
	const senderIds = getMessageSenderCandidates(message)
	const decryptedText = await decryptMessageText(senderIds, text)
	return normalizeSdkMessageToLegacy({
		...message,
		text: decryptedText,
		content: decryptedText,
		textContent: {
			...(message.textContent || {}),
			content: decryptedText
		}
	})
}

function parseExtension(value) {
	return parseJson(value, {})
}

function normalizeSdkUserToLegacy(user = {}) {
	const extension = parseExtension(user.serverExtension)
	return {
		id: user.accountId,
		accountId: user.accountId,
		imAccid: user.accountId,
		userName: extension.userName || user.accountId,
		nickName: user.name || user.accountId,
		showNickName: user.name || user.accountId,
		headImage: user.avatar || '',
		headImageThumb: user.avatar || '',
		phone: user.mobile || '',
		email: user.email || '',
		online: false,
		onlineWeb: false,
		onlineApp: false,
		onlineClients: {},
		deleted: false,
		sourceUser: user
	}
}

function normalizeSdkFriendToLegacy(friend = {}) {
	const user = normalizeSdkUserToLegacy(friend.userProfile || { accountId: friend.accountId })
	return {
		...user,
		remarkNickName: friend.alias || '',
		showNickName: friend.alias || user.showNickName,
		serverExtension: friend.serverExtension || '',
		customerExtension: friend.customerExtension || '',
		sourceFriend: friend
	}
}

function normalizeSdkUserStatusToLegacy(status = {}) {
	const accountId = status.accountId || status.account || status.accid || ''
	const statusType = Number(status.statusType || status.type || 0)
	const clientType = Number(status.clientType || status.terminalType || 0)
	const isOnline = statusType === USER_STATUS_TYPE_LOGIN
	const isOffline = statusType === USER_STATUS_TYPE_LOGOUT || statusType === USER_STATUS_TYPE_DISCONNECT
	const isWebClient = clientType === LOGIN_CLIENT_TYPE_PC || clientType === LOGIN_CLIENT_TYPE_WEB || clientType === LOGIN_CLIENT_TYPE_MAC_OS
	const isAppClient = clientType === LOGIN_CLIENT_TYPE_ANDROID || clientType === LOGIN_CLIENT_TYPE_IOS || clientType === LOGIN_CLIENT_TYPE_HARMONY_OS

	return {
		accountId,
		userId: accountId,
		lastLoginIp: status.lastLoginIp || status.loginIp || status.ipAddress || status.ip || status.clientIp || '',
		lastLoginArea: status.lastLoginArea || status.loginArea || status.ipArea || status.area || '',
		clientType,
		terminal: isAppClient ? 1 : 0,
		online: isOnline,
		isOffline,
		onlineWeb: isWebClient ? isOnline : undefined,
		onlineApp: isAppClient ? isOnline : undefined,
		statusType,
		publishTime: status.publishTime || 0,
		sourceStatus: status
	}
}

function splitAccountIds(accountIds = [], size = 100) {
	const list = [...new Set((accountIds || []).filter(Boolean).map(item => String(item)))]
	const chunks = []
	for (let i = 0; i < list.length; i += size) {
		chunks.push(list.slice(i, i + size))
	}
	return chunks
}

function normalizeSdkApplicationToLegacy(application = {}, profileMap = {}) {
	const sendProfile = profileMap[application.applicantAccountId] || {}
	const recvProfile = profileMap[application.recipientAccountId] || {}
	const timestamp = Number(application.timestamp || Date.now())
	return {
		id: application.id || `${application.applicantAccountId}_${application.recipientAccountId}_${timestamp}`,
		sendId: application.applicantAccountId,
		recvId: application.recipientAccountId,
		sendNickName: sendProfile.nickName || application.applicantAccountId,
		recvNickName: recvProfile.nickName || application.recipientAccountId,
		sendHeadImage: sendProfile.headImage || '',
		recvHeadImage: recvProfile.headImage || '',
		remark: application.postscript || '',
		status: application.status,
		read: !!application.read,
		timestamp,
		sourceApplication: application
	}
}

function getConversationTargetId(conversation = {}) {
	try {
		return conversation.conversationId && yunxinIM.nim?.V2NIMConversationIdUtil?.parseConversationTargetId
			? yunxinIM.nim.V2NIMConversationIdUtil.parseConversationTargetId(conversation.conversationId)
			: ''
	} catch (error) {
		const parts = String(conversation.conversationId || '').split('|')
		return parts[parts.length - 1] || ''
	}
}

function getLegacyChatType(conversation = {}) {
	const type = Number(conversation.type || 0)
	if (type === 1) {
		return 'PRIVATE'
	}
	if (type === 2) {
		return 'GROUP'
	}
	return 'UNKNOWN'
}

function getLastMessageContent(lastMessage = {}) {
	const messageType = resolveSdkMessageType(lastMessage)
	if (Number(lastMessage.lastMessageState) === 1) {
		return '撤回了一条消息'
	}
	if (messageType === 1) {
		return '[图片]'
	}
	if (messageType === 2) {
		return '[语音]'
	}
	if (messageType === 3) {
		return '[视频]'
	}
	if (messageType === 6) {
		return '[文件]'
	}
	if (messageType === 100) {
		return '[自定义消息]'
	}
	return lastMessage.text || ''
}

function getSdkTeamName(team = {}) {
	return team.name || team.teamName || team.groupName || team.showName || team.showGroupName || ''
}

function getSdkTeamAvatar(team = {}) {
	return team.avatar || team.icon || team.teamAvatar || team.headImage || team.headImageThumb || ''
}

async function normalizeCloudConversationToLegacy(conversation = {}) {
	const type = getLegacyChatType(conversation)
	if (type === 'UNKNOWN') {
		return null
	}
	const lastMessage = conversation.lastMessage || {}
	let lastContent = getLastMessageContent(lastMessage)
	if (isEncryptedMessageText(lastContent)) {
		lastContent = await decryptMessageText(getMessageSenderCandidates(lastMessage, getConversationTargetId(conversation)), lastContent)
	}
	const targetId = getConversationTargetId(conversation)
	const lastMessageTime = Number(lastMessage.messageRefer?.createTime || conversation.sortOrder || conversation.updateTime || conversation.createTime || Date.now())
	const conversationExtension = parseExtension(conversation.serverExtension)
	const remarkGroupName = type === 'GROUP' ? (conversationExtension.remarkGroupName || '') : ''
	return {
		conversationId: conversation.conversationId,
		type,
		targetId,
		showName: remarkGroupName || conversation.name || targetId,
		remarkGroupName,
		headImage: conversation.avatar || '',
		isDnd: !!conversation.mute,
		isTop: !!conversation.stickTop,
		cloudIsTop: !!conversation.stickTop,
		lastContent,
		lastSendTime: lastMessageTime,
		optTime: lastMessageTime,
		unreadCount: Number(conversation.unreadCount || 0),
		hotMinIdx: 0,
		readedMessageIdx: 0,
		lastReadTime: Number(conversation.lastReadTime || 0),
		messages: [],
		atMe: false,
		atAll: false,
		lastAtMessageId: -1,
		stored: false,
		delete: false,
		sourceConversation: conversation
	}
}

class YunxinIMService {
	constructor() {
		this.nim = null
		this.isInitialized = false
		this.currentAccount = ''
		this.loginStatus = 0
		this._initPromise = null
		this.listeners = {}
		this._messageListenerBound = false
		this._lastReadReceiptTime = 0
		this._lastReadReceiptMessageKey = ''
		this._readReceiptCooldown = 3000
		this._subscribedUserStatusAccountIds = new Set()
	}

	on(eventName, callback) {
		if (!eventName || typeof callback !== 'function') {
			return () => {}
		}
		if (!this.listeners[eventName]) {
			this.listeners[eventName] = []
		}
		this.listeners[eventName].push(callback)
		return () => this.off(eventName, callback)
	}

	off(eventName, callback) {
		if (!this.listeners[eventName]) {
			return
		}
		this.listeners[eventName] = this.listeners[eventName].filter(item => item !== callback)
	}

	emit(eventName, payload) {
		(this.listeners[eventName] || []).slice().forEach(callback => {
			try {
				callback(payload)
			} catch (error) {
				console.warn('[YunxinIM] 事件回调执行失败:', eventName, error)
			}
		})
	}

	getSdkLoginStatus() {
		try {
			return this.nim?.V2NIMLoginService?.getLoginStatus?.() || this.loginStatus || 0
		} catch (error) {
			return this.loginStatus || 0
		}
	}

	isLoggedIn() {
		return this.getSdkLoginStatus() === 1
	}

	async init(loginInfo = {}) {
		const normalizedLoginInfo = normalizeLoginInfo(loginInfo)
		if (!normalizedLoginInfo.account || !normalizedLoginInfo.token) {
			throw new Error('初始化云信 SDK 缺少 account 或 token')
		}
		if (!getAppKey()) {
			throw new Error('初始化云信 SDK 缺少 VUE_APP_YUNXIN_APP_KEY')
		}
		if (this._initPromise) {
			return this._initPromise
		}
		this._initPromise = this._doInit(normalizedLoginInfo)
		try {
			return await this._initPromise
		} finally {
			this._initPromise = null
		}
	}

	async _doInit(loginInfo) {
		if (this.isInitialized && this.currentAccount === loginInfo.account && this.isLoggedIn()) {
			return this.nim
		}
		if (this.isInitialized && this.currentAccount && this.currentAccount !== loginInfo.account) {
			await this.logout()
		}

		if (!this.nim) {
			const { nimOptions, otherOptions } = buildNimInitOptions()
			this.nim = NIM.getInstance(nimOptions, otherOptions)
			this._registerListeners()
		}

		localStorage.setItem(STORAGE_KEY, JSON.stringify({
			account: loginInfo.account,
			token: loginInfo.token
		}))

		await this.nim.V2NIMLoginService.login(loginInfo.account, loginInfo.token)
		this.currentAccount = loginInfo.account
		this.loginStatus = this.getSdkLoginStatus()
		this.isInitialized = true
		console.log('[YunxinIM] SDK 初始化并登录成功:', this.currentAccount)
		return this.nim
	}

	_registerListeners() {
		if (!this.nim || this._messageListenerBound) {
			return
		}
		this._messageListenerBound = true

		this.nim.V2NIMLoginService?.on?.('onLoginStatus', status => {
			this.loginStatus = status
			this.emit('loginStatus', status)
		})
		this.nim.V2NIMLoginService?.on?.('onDisconnected', error => {
			this.emit('disconnected', error)
		})
		this.nim.V2NIMLoginService?.on?.('onKickedOffline', event => {
			this.emit('kickedOffline', event)
		})
		this.nim.V2NIMMessageService?.on?.('onReceiveMessages', messages => {
			const list = Array.isArray(messages) ? messages : []
			list.forEach(message => this.emit('newMessage', message))
			this.emit('messages', list)
		})
		this.nim.V2NIMMessageService?.on?.('syncMessage', messages => {
			this.emit('syncMessages', Array.isArray(messages) ? messages : [])
		})
		this.nim.V2NIMConversationService?.on?.('onConversationDeleted', conversationIds => {
			this.emit('conversationDeleted', Array.isArray(conversationIds) ? conversationIds : [])
		})
		this.nim.V2NIMMessageService?.on?.('onReceiveP2PMessageReadReceipts', receipts => {
			this.emit('p2pMessageReadReceipt', Array.isArray(receipts) ? receipts : [])
		})
		this.nim.V2NIMMessageService?.on?.('onReceiveTeamMessageReadReceipts', receipts => {
			this.emit('teamMessageReadReceipt', Array.isArray(receipts) ? receipts : [])
		})
		this.nim.V2NIMMessageService?.on?.('onMessageRevokeNotifications', notifications => {
			const list = Array.isArray(notifications) ? notifications : []
			this.emit('messageRevokeNotifications', list.map(item => normalizeSdkRevokeNotificationToLegacy(item, this.currentAccount)))
		})
		this.nim.V2NIMNotificationService?.on?.('onReceiveCustomNotifications', notifications => {
			this.emit('customNotification', Array.isArray(notifications) ? notifications : [])
		})
		this.nim.V2NIMFriendService?.on?.('onFriendAdded', friend => {
			this.emit('friendAdded', normalizeSdkFriendToLegacy(friend))
		})
		this.nim.V2NIMFriendService?.on?.('onFriendDeleted', (accountId, deletionType) => {
			this.emit('friendDeleted', { accountId, deletionType })
		})
		this.nim.V2NIMFriendService?.on?.('onFriendAddApplication', async application => {
			this.emit('friendAddApplication', await this.normalizeFriendApplication(application))
		})
		this.nim.V2NIMFriendService?.on?.('onFriendAddRejected', async application => {
			this.emit('friendAddRejected', await this.normalizeFriendApplication(application))
		})
		this.nim.V2NIMFriendService?.on?.('onFriendInfoChanged', friend => {
			this.emit('friendInfoChanged', normalizeSdkFriendToLegacy(friend))
		})
		this.nim.V2NIMSubscriptionService?.on?.('onUserStatusChanged', userStatusList => {
			const list = Array.isArray(userStatusList) ? userStatusList : []
			this.emit('userStatusChanged', list.map(item => normalizeSdkUserStatusToLegacy(item)))
		})
		this.nim.V2NIMTeamService?.on?.('onReceiveTeamJoinActionInfo', joinActionInfo => {
			this.emit('teamJoinActionInfo', joinActionInfo)
		})
		this.nim.V2NIMTeamService?.on?.('onTeamJoined', team => {
			this.emit('teamJoined', team)
		})
		this.nim.V2NIMTeamService?.on?.('onTeamLeft', (team, isKicked) => {
			this.emit('teamLeft', { team, isKicked })
		})
		this.nim.V2NIMTeamService?.on?.('onTeamDismissed', team => {
			this.emit('teamDismissed', team)
		})
		this.nim.V2NIMTeamService?.on?.('onTeamInfoUpdated', team => {
			this.emit('teamInfoUpdated', team)
		})
	}

	getStoredLoginInfo() {
		try {
			return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
		} catch (error) {
			return {}
		}
	}

	async initFromStorage() {
		const storedLoginInfo = this.getStoredLoginInfo()
		if (!storedLoginInfo.account || !storedLoginInfo.token) {
			return false
		}
		await this.init(storedLoginInfo)
		return true
	}

	async subscribeUserStatus(accountIds = [], options = {}) {
		if (!this.nim?.V2NIMSubscriptionService) {
			console.warn('[YunxinIM] 当前 SDK 未启用用户状态订阅服务')
			return []
		}
		const duration = Number(options.duration || 60 * 60 * 24)
		const immediateSync = options.immediateSync !== false
		const failedAccountIds = []
		for (const ids of splitAccountIds(accountIds)) {
			try {
				const failed = await this.nim.V2NIMSubscriptionService.subscribeUserStatus({
					accountIds: ids,
					duration,
					immediateSync
				})
				ids.forEach(id => this._subscribedUserStatusAccountIds.add(id))
				failedAccountIds.push(...(Array.isArray(failed) ? failed : []))
			} catch (error) {
				console.warn('[YunxinIM] 订阅用户在线状态失败:', error)
				failedAccountIds.push(...ids)
			}
		}
		return failedAccountIds
	}

	async unsubscribeUserStatus(accountIds = []) {
		if (!this.nim?.V2NIMSubscriptionService) {
			return []
		}
		const targetIds = accountIds && accountIds.length
			? [...new Set(accountIds.filter(Boolean).map(item => String(item)))]
			: [...this._subscribedUserStatusAccountIds]
		const failedAccountIds = []
		for (const ids of splitAccountIds(targetIds)) {
			try {
				const failed = await this.nim.V2NIMSubscriptionService.unsubscribeUserStatus({ accountIds: ids })
				ids.forEach(id => this._subscribedUserStatusAccountIds.delete(id))
				failedAccountIds.push(...(Array.isArray(failed) ? failed : []))
			} catch (error) {
				console.warn('[YunxinIM] 取消订阅用户在线状态失败:', error)
				failedAccountIds.push(...ids)
			}
		}
		return failedAccountIds
	}

	async getJoinedTeamList(teamTypes = [1, 2]) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		try {
			const teams = await this.nim.V2NIMTeamService.getJoinedTeamList(teamTypes)
			console.log('[YunxinIM] 获取已加入群组成功, 数量:', teams?.length || 0)
			return teams || []
		} catch (error) {
			console.error('[YunxinIM] 获取已加入群组失败:', error)
			throw error
		}
	}

	async getTeamInfo(teamId, teamType = 1) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		const finalTeamType = Number(teamType) || 1
		try {
			const team = await this.nim.V2NIMTeamService.getTeamInfo(String(teamId), finalTeamType)
			console.log('[YunxinIM] 获取群详情成功:', teamId)
			return team
		} catch (error) {
			console.error('[YunxinIM] 获取群详情失败:', teamId, error)
			throw error
		}
	}

	async getTeamMemberList(teamId, teamType = 1, queryOption = {}) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		const finalTeamType = Number(teamType) || 1
		try {
			const getMembersFromResult = result => Array.isArray(result) ? result : (result?.memberList || result?.members || [])
			const allMembers = []
			const pageSize = Math.min(Math.max(Number(queryOption.limit) || 100, 1), 100)
			let nextToken = typeof queryOption.nextToken === 'string' ? queryOption.nextToken : ''
			let page = 0
			let lastToken = null

			do {
				const finalQueryOption = {
					roleQueryType: typeof queryOption.roleQueryType === 'number' ? queryOption.roleQueryType : 0,
					onlyChatBanned: false,
					direction: 0,
					...queryOption,
					limit: pageSize,
					nextToken
				}
				const result = await this.nim.V2NIMTeamService.getTeamMemberList(String(teamId), finalTeamType, finalQueryOption)
				const members = getMembersFromResult(result)
				allMembers.push(...members)
				lastToken = nextToken
				nextToken = Array.isArray(result) ? '' : (result?.nextToken || result?.nextPageToken || '')
				page++
				if (queryOption.nextToken || queryOption.pageAll === false || result?.finished !== false || !members.length) {
					break
				}
			} while (nextToken && nextToken !== lastToken && page < 100)

			if (!allMembers.length && !queryOption.nextToken && queryOption.pageAll !== false) {
				const fallbackResult = await this.nim.V2NIMTeamService.getTeamMemberList(String(teamId), finalTeamType, {
					roleQueryType: typeof queryOption.roleQueryType === 'number' ? queryOption.roleQueryType : 0,
					limit: 200,
					nextToken: ''
				})
				allMembers.push(...getMembersFromResult(fallbackResult))
			}

			console.log('[YunxinIM] 获取群成员成功:', teamId, allMembers.length)
			return allMembers
		} catch (error) {
			console.error('[YunxinIM] 获取群成员失败:', teamId, error)
			throw error
		}
	}

	async createTeam(options = {}) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		try {
			const result = await this.nim.V2NIMTeamService.createTeam({
				name: options.name,
				memberLimit: options.memberLimit,
				avatar: options.avatar || '',
				intro: options.intro || '',
				announcement: options.announcement || '',
				serverExtension: options.serverExtension || '',
				teamType: options.teamType || 1,
				joinMode: typeof options.joinMode === 'number' ? options.joinMode : 0,
				agreeMode: typeof options.agreeMode === 'number' ? options.agreeMode : 0,
				inviteMode: typeof options.inviteMode === 'number' ? options.inviteMode : 0,
				updateInfoMode: typeof options.updateInfoMode === 'number' ? options.updateInfoMode : 0,
				updateExtensionMode: typeof options.updateExtensionMode === 'number' ? options.updateExtensionMode : 0,
				chatBannedMode: typeof options.chatBannedMode === 'number' ? options.chatBannedMode : 0
			}, options.members || [], options.postscript || '')
			const team = result?.team || result
			console.log('[YunxinIM] 群组创建成功')
			return team
		} catch (error) {
			console.error('[YunxinIM] 群组创建失败:', error)
			throw error
		}
	}

	async updateTeamInfo(teamId, teamType = 1, updateParams = {}) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		if (!updateParams || !Object.keys(updateParams).length) {
			throw new Error('缺少群信息更新参数')
		}
		const finalTeamType = Number(teamType) || 1
		try {
			await this.nim.V2NIMTeamService.updateTeamInfo(String(teamId), finalTeamType, updateParams)
			console.log('[YunxinIM] 更新群信息成功:', teamId, updateParams)
		} catch (error) {
			console.error('[YunxinIM] 更新群信息失败:', teamId, updateParams, error)
			throw error
		}
	}

	async updateSelfTeamMemberInfo(teamId, teamType = 1, memberInfoParams = {}) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		if (!memberInfoParams || !Object.keys(memberInfoParams).length) {
			throw new Error('缺少群成员信息更新参数')
		}
		const finalTeamType = Number(teamType) || 1
		try {
			await this.nim.V2NIMTeamService.updateSelfTeamMemberInfo(String(teamId), finalTeamType, memberInfoParams)
			console.log('[YunxinIM] 更新自己群成员信息成功:', teamId, memberInfoParams)
		} catch (error) {
			console.error('[YunxinIM] 更新自己群成员信息失败:', teamId, memberInfoParams, error)
			throw error
		}
	}

	async updateTeamConversationExtension(teamId, extension = {}) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		const conversationId = this.getConversationId(teamId, 'team')
		const serverExtension = typeof extension === 'string' ? extension : JSON.stringify(extension || {})
		try {
			await this.nim.V2NIMConversationService.updateConversation(conversationId, { serverExtension })
			console.log('[YunxinIM] 更新群会话扩展成功:', teamId, serverExtension)
		} catch (error) {
			console.error('[YunxinIM] 更新群会话扩展失败:', teamId, error)
			throw error
		}
	}

	async inviteTeamMembers(teamId, teamType = 1, accountIds = [], postscript = '') {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		const inviteeAccountIds = [...new Set(accountIds.filter(Boolean).map(item => String(item)))]
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		if (!inviteeAccountIds.length) {
			throw new Error('请选择要邀请的成员')
		}
		try {
			const failedAccountIds = await this.nim.V2NIMTeamService.inviteMember(
				String(teamId),
				teamType,
				inviteeAccountIds,
				postscript
			)
			console.log('[YunxinIM] 邀请群成员成功:', teamId, inviteeAccountIds.length)
			return failedAccountIds || []
		} catch (error) {
			console.error('[YunxinIM] 邀请群成员失败:', teamId, error)
			throw error
		}
	}

	async leaveTeam(teamId, teamType = 1) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		try {
			await this.nim.V2NIMTeamService.leaveTeam(String(teamId), Number(teamType) || 1)
			console.log('[YunxinIM] 退出群聊成功:', teamId)
		} catch (error) {
			console.error('[YunxinIM] 退出群聊失败:', teamId, error)
			throw error
		}
	}

	async dismissTeam(teamId, teamType = 1) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		try {
			await this.nim.V2NIMTeamService.dismissTeam(String(teamId), Number(teamType) || 1)
			console.log('[YunxinIM] 解散群聊成功:', teamId)
		} catch (error) {
			console.error('[YunxinIM] 解散群聊失败:', teamId, error)
			throw error
		}
	}

	async getTeamJoinActionInfos(types = [], status = [], limit = 50) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		try {
			const result = await this.nim.V2NIMTeamService.getTeamJoinActionInfoList({
				types,
				status,
				offset: 0,
				limit
			})
			return result?.infos || []
		} catch (error) {
			console.error('[YunxinIM] 获取群通知失败:', error)
			throw error
		}
	}

	buildTeamInvitationInfo(invitationInfo = {}) {
		const source = invitationInfo.sourceActionInfo || invitationInfo
		return {
			teamId: String(source.teamId || invitationInfo.teamId || ''),
			teamType: Number(source.teamType || invitationInfo.teamType || 1),
			operatorAccountId: String(source.operatorAccountId || invitationInfo.operatorAccountId || ''),
			actionType: 2
		}
	}

	async acceptTeamInvitation(invitationInfo) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		const finalInvitationInfo = this.buildTeamInvitationInfo(invitationInfo)
		if (!finalInvitationInfo.teamId || !finalInvitationInfo.operatorAccountId) {
			throw new Error('入群邀请参数不完整')
		}
		try {
			return await this.nim.V2NIMTeamService.acceptInvitation(finalInvitationInfo)
		} catch (error) {
			console.error('[YunxinIM] 同意入群邀请失败:', error)
			throw error
		}
	}

	async rejectTeamInvitation(invitationInfo, postscript = '') {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		const finalInvitationInfo = this.buildTeamInvitationInfo(invitationInfo)
		if (!finalInvitationInfo.teamId || !finalInvitationInfo.operatorAccountId) {
			throw new Error('入群邀请参数不完整')
		}
		try {
			await this.nim.V2NIMTeamService.rejectInvitation(finalInvitationInfo, postscript)
		} catch (error) {
			console.error('[YunxinIM] 拒绝入群邀请失败:', error)
			throw error
		}
	}

	async setTeamJoinActionInfoRead(actionInfo) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		try {
			await this.nim.V2NIMTeamService.setTeamJoinActionInfoRead(actionInfo?.sourceActionInfo || actionInfo)
		} catch (error) {
			console.warn('[YunxinIM] 设置群通知已读失败:', error)
		}
	}

	async kickTeamMembers(teamId, teamType = 1, accountIds = []) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		const memberAccountIds = [...new Set(accountIds.filter(Boolean).map(item => String(item)))]
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		if (!memberAccountIds.length) {
			throw new Error('请选择要移除的成员')
		}
		try {
			await this.nim.V2NIMTeamService.kickMember(String(teamId), teamType, memberAccountIds)
			console.log('[YunxinIM] 移除群成员成功:', teamId, memberAccountIds.length)
		} catch (error) {
			console.error('[YunxinIM] 移除群成员失败:', teamId, error)
			throw error
		}
	}

	async setTeamMembersMute(teamId, teamType = 1, accountIds = [], chatBanned = true) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		const memberAccountIds = [...new Set(accountIds.filter(Boolean).map(item => String(item)))]
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		if (!memberAccountIds.length) {
			throw new Error('请选择要操作的成员')
		}
		try {
			await Promise.all(memberAccountIds.map(accountId => {
				return this.nim.V2NIMTeamService.setTeamMemberChatBannedStatus(
					String(teamId),
					teamType,
					accountId,
					!!chatBanned
				)
			}))
			console.log('[YunxinIM] 设置群成员禁言成功:', teamId, memberAccountIds.length, !!chatBanned)
		} catch (error) {
			console.error('[YunxinIM] 设置群成员禁言失败:', teamId, error)
			throw error
		}
	}

	async setTeamChatBannedMode(teamId, teamType = 1, isAllMuted = false) {
		if (!this.isInitialized) {
			throw new Error('IM SDK未初始化')
		}
		if (!teamId) {
			throw new Error('群ID不能为空')
		}
		const finalTeamType = Number(teamType) || 1
		const modeEnum = this.nim?.V2NIMTeamChatBannedMode || {}
		const chatBannedMode = isAllMuted
			? (modeEnum.V2NIM_TEAM_CHAT_BANNED_MODE_BANNED_NORMAL ?? 1)
			: (modeEnum.V2NIM_TEAM_CHAT_BANNED_MODE_UNBAN ?? 0)
		try {
			await this.nim.V2NIMTeamService.setTeamChatBannedMode(String(teamId), finalTeamType, chatBannedMode)
			console.log('[YunxinIM] 设置群禁言模式成功:', teamId, chatBannedMode)
		} catch (error) {
			console.error('[YunxinIM] 设置群禁言模式失败:', teamId, error)
			throw error
		}
	}

	async logout() {
		try {
			await this.nim?.V2NIMLoginService?.logout?.()
		} catch (error) {
			console.warn('[YunxinIM] logout 失败:', error)
		}
		try {
			this.nim?.destroy?.()
		} catch (error) {
			console.warn('[YunxinIM] destroy 失败:', error)
		}
		this.nim = null
		this.isInitialized = false
		this.currentAccount = ''
		this.loginStatus = 0
		this._messageListenerBound = false
		this._lastReadReceiptTime = 0
		this._lastReadReceiptMessageKey = ''
		this._subscribedUserStatusAccountIds.clear()
		localStorage.removeItem(STORAGE_KEY)
	}

	async uploadFile(fileObj, sceneName = 'nim_default_im', onProgress = null) {
		if (!this.nim?.V2NIMStorageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		if (!fileObj) {
			throw new Error('上传文件不能为空')
		}
		const task = this.nim.V2NIMStorageService.createUploadFileTask({
			fileObj,
			sceneName
		})
		return this.nim.V2NIMStorageService.uploadFile(task, onProgress || (() => {}))
	}

	async updateSelfUserProfile(profile = {}) {
		if (!this.nim?.V2NIMUserService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		const updateParams = {
			name: profile.name || '',
			avatar: profile.avatar || '',
			sign: profile.sign || '',
			gender: typeof profile.gender === 'number' ? profile.gender : 0
		}
		return this.nim.V2NIMUserService.updateSelfUserProfile(updateParams)
	}

	async getUserProfiles(accountIds = []) {
		if (!this.nim?.V2NIMUserService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		const ids = [...new Set(accountIds.filter(Boolean).map(item => String(item)))]
		if (!ids.length) {
			return []
		}
		const users = []
		for (let i = 0; i < ids.length; i += 100) {
			const list = await this.nim.V2NIMUserService.getUserList(ids.slice(i, i + 100))
			users.push(...(list || []))
		}
		return (users || []).map(normalizeSdkUserToLegacy)
	}

	async normalizeFriendApplication(application = {}) {
		const profiles = await this.getUserProfiles([
			application.applicantAccountId,
			application.recipientAccountId
		]).catch(() => [])
		const profileMap = profiles.reduce((map, profile) => {
			map[profile.accountId] = profile
			return map
		}, {})
		return normalizeSdkApplicationToLegacy(application, profileMap)
	}

	async getFriendList() {
		if (!this.nim?.V2NIMFriendService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		const friends = await this.nim.V2NIMFriendService.getFriendList()
		return (friends || []).map(normalizeSdkFriendToLegacy)
	}

	async getCloudConversationList(limit = 100) {
		if (!this.nim?.V2NIMConversationService) {
			throw new Error('云信云端会话服务尚未初始化')
		}
		let offset = 0
		let finished = false
		const conversations = []
		while (!finished) {
			const result = await this.nim.V2NIMConversationService.getConversationList(offset, limit)
			conversations.push(...(result?.conversationList || []))
			offset = result?.offset || 0
			finished = !!result?.finished || !offset
		}
		console.log('[YunxinIM] 云端会话列表:', conversations.map(item => ({
			conversationId: item.conversationId,
			type: item.type,
			name: item.name,
			lastMessage: item.lastMessage,
			updateTime: item.updateTime,
			sortOrder: item.sortOrder
		})))
		const chats = (await Promise.all(conversations.map(item => normalizeCloudConversationToLegacy(item)))).filter(Boolean)
		return this.fillCloudConversationProfiles(chats)
	}

	async fillCloudConversationProfiles(chats = []) {
		const p2pAccountIds = chats
			.filter(chat => chat.type === 'PRIVATE' && chat.targetId)
			.map(chat => chat.targetId)
		const profiles = p2pAccountIds.length ? await this.getUserProfiles(p2pAccountIds).catch(error => {
			console.warn('[YunxinIM] 补全会话用户资料失败:', error)
			return []
		}) : []
		const profileMap = profiles.reduce((map, profile) => {
			map[String(profile.accountId || profile.id)] = profile
			return map
		}, {})
		await Promise.all(chats.filter(chat => chat.type === 'GROUP' && chat.targetId).map(async chat => {
			let team = await this.getTeamInfo(chat.targetId, 1).catch(() => null)
			if (!team) {
				team = await this.getTeamInfo(chat.targetId, 2).catch(error => {
					console.warn('[YunxinIM] 补全会话群资料失败:', chat.targetId, error)
					return null
				})
			}
			if (team) {
				const teamName = getSdkTeamName(team)
				const teamAvatar = getSdkTeamAvatar(team)
				chat.showName = teamName || chat.showName || chat.targetId
				chat.headImage = teamAvatar || chat.headImage || ''
				chat.sourceTeam = team
				console.log('[YunxinIM] 补全会话群资料:', chat.targetId, chat.showName)
			}
		}))
		return chats.map(chat => {
			if (chat.type !== 'PRIVATE') {
				return chat
			}
			const profile = profileMap[String(chat.targetId)]
			if (profile) {
				chat.showName = profile.showNickName || profile.nickName || profile.accountId || chat.showName
				chat.headImage = profile.headImage || profile.headImageThumb || chat.headImage || ''
				chat.sourceUser = profile.sourceUser || profile
			}
			return chat
		})
	}

	async getRecentMessages(chatInfo = {}, limit = 50) {
		if (!this.nim?.V2NIMMessageService?.getMessageList) {
			throw new Error('云信消息服务尚未初始化')
		}
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		const messages = await this.nim.V2NIMMessageService.getMessageList({
			conversationId,
			limit,
			direction: 0
		})
		const legacyMessages = await Promise.all((messages || []).map(message => normalizeSdkMessageToLegacyAsync(message)))
		return legacyMessages.reverse().map(message => ({
			...message,
			selfSend: String(message.sendId) === String(this.currentAccount),
			status: String(message.sendId) === String(this.currentAccount) && this.isPeerRead(message.sourceMessage)
				? MESSAGE_STATUS.READED
				: message.status
		}))
	}

	async getHistoryMessages(chatInfo = {}, anchorMessage, limit = 50) {
		if (!anchorMessage?.sourceMessage) {
			return []
		}
		if (!this.nim?.V2NIMMessageService?.getMessageList) {
			throw new Error('云信消息服务尚未初始化')
		}
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		const messages = await this.nim.V2NIMMessageService.getMessageList({
			conversationId,
			anchorMessage: anchorMessage.sourceMessage,
			limit,
			direction: 0
		})
		const legacyMessages = await Promise.all((messages || []).map(message => normalizeSdkMessageToLegacyAsync(message)))
		return legacyMessages.reverse().map(message => ({
			...message,
			selfSend: String(message.sendId) === String(this.currentAccount),
			status: String(message.sendId) === String(this.currentAccount) && this.isPeerRead(message.sourceMessage)
				? MESSAGE_STATUS.READED
				: message.status
		}))
	}

	async markConversationRead(chatInfo = {}) {
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		if (!conversationId) {
			throw new Error('会话ID为空，无法清除未读数')
		}
		console.log('[YunxinIM] 开始清除会话未读数:', {
			conversationId,
			type: chatInfo.type,
			targetId: chatInfo.targetId,
			unreadCount: chatInfo.unreadCount
		})
		const isGroup = chatInfo.type === 'GROUP' || this.parseConversationType(conversationId) === 2
		let receiptMessages = []
		let clearSuccess = false
		const runReadAction = async (actionName, action) => {
			try {
				const result = await action()
				console.log(`[YunxinIM] ${actionName}返回:`, conversationId, result)
				const failedItems = Array.isArray(result) ? result.filter(item => item?.error || item?.code || item?.conversationId) : []
				if (failedItems.length) {
					console.warn(`[YunxinIM] ${actionName}存在失败项:`, conversationId, failedItems)
					return false
				}
				return true
			} catch (error) {
				console.warn(`[YunxinIM] ${actionName}失败:`, conversationId, error)
				return false
			}
		}
		if (!isGroup) {
			await this.sendP2PReadReceipt(chatInfo).catch(error => {
				console.warn('[YunxinIM] 发送单聊已读回执失败:', error)
			})
		} else {
			receiptMessages = await this.sendTeamReadReceipts(chatInfo).catch(error => {
				console.warn('[YunxinIM] 发送群聊已读回执失败:', error)
				return []
			})
		}
		if (this.nim.V2NIMConversationService?.clearUnreadCountByIds) {
			clearSuccess = await runReadAction('云端清除未读数', () => this.nim.V2NIMConversationService.clearUnreadCountByIds([conversationId])) || clearSuccess
		}
		if (this.nim.V2NIMLocalConversationService?.clearUnreadCountByIds) {
			clearSuccess = await runReadAction('本地清除未读数', () => this.nim.V2NIMLocalConversationService.clearUnreadCountByIds([conversationId])) || clearSuccess
		}
		if (!clearSuccess) {
			throw new Error('云信清除会话未读数未成功执行')
		}
		const latestConversation = await this.nim.V2NIMConversationService?.getConversation?.(conversationId).catch(error => {
			console.warn('[YunxinIM] 查询清除后的云端会话失败:', conversationId, error)
			return null
		})
		if (Number(latestConversation?.unreadCount || 0) > 0) {
			console.warn('[YunxinIM] 清除未读后仍有未读数:', conversationId, latestConversation.unreadCount, latestConversation)
		} else {
			console.log('[YunxinIM] 清除未读后云端会话未读数:', conversationId, Number(latestConversation?.unreadCount || 0), latestConversation)
		}
		return receiptMessages
	}

	async sendP2PReadReceipt(chatInfo = {}) {
		if (!this.nim?.V2NIMMessageService?.sendP2PMessageReceipt) {
			return
		}
		const messages = Array.isArray(chatInfo.messages) ? chatInfo.messages : []
		for (let i = messages.length - 1; i >= 0; i -= 1) {
			const message = messages[i]
			if (!message || message.selfSend) {
				continue
			}
			const sourceMessage = message.sourceMessage || message
			if (sourceMessage?.conversationId || sourceMessage?.messageClientId || sourceMessage?.messageServerId) {
				const now = Date.now()
				const messageKey = sourceMessage.messageClientId || sourceMessage.messageServerId || message.id || ''
				if (messageKey && messageKey === this._lastReadReceiptMessageKey && now - this._lastReadReceiptTime < this._readReceiptCooldown) {
					return
				}
				await this.nim.V2NIMMessageService.sendP2PMessageReceipt(sourceMessage)
				this._lastReadReceiptTime = now
				this._lastReadReceiptMessageKey = messageKey
				return
			}
		}
	}

	async sendTeamReadReceipts(chatInfo = {}) {
		if (!this.nim?.V2NIMMessageService?.sendTeamMessageReceipts) {
			return []
		}
		const messages = Array.isArray(chatInfo.messages) ? chatInfo.messages : []
		const receiptMessages = []
		const sourceMessages = []
		for (let i = messages.length - 1; i >= 0 && sourceMessages.length < 50; i -= 1) {
			const message = messages[i]
			if (!message || message.selfSend || !message.receipt || message.receiptOk) {
				continue
			}
			const sourceMessage = message.sourceMessage || message
			if (!sourceMessage?.conversationId || (!sourceMessage.messageClientId && !sourceMessage.messageServerId)) {
				continue
			}
			sourceMessages.unshift(sourceMessage)
			receiptMessages.unshift(message)
		}
		if (!sourceMessages.length) {
			return []
		}
		await this.nim.V2NIMMessageService.sendTeamMessageReceipts(sourceMessages)
		return receiptMessages.map(message => ({
			id: message.id,
			tmpId: message.tmpId,
			receiptOk: true
		}))
	}

	async getTeamMessageReceiptDetail(msgInfo = {}, memberAccountIds = []) {
		if (!this.nim?.V2NIMMessageService?.getTeamMessageReceiptDetail) {
			throw new Error('云信消息回执服务尚未初始化')
		}
		const sourceMessage = msgInfo.sourceMessage || msgInfo
		if (!sourceMessage?.messageClientId && !sourceMessage?.messageServerId) {
			throw new Error('缺少云信消息对象，无法查询群消息回执')
		}
		const detail = await this.nim.V2NIMMessageService.getTeamMessageReceiptDetail(sourceMessage, memberAccountIds)
		return {
			readAccountList: detail?.readAccountList || [],
			unreadAccountList: detail?.unreadAccountList || [],
			readReceipt: detail?.readReceipt || {}
		}
	}

	async recallLegacyMessage(msgInfo = {}, params = {}) {
		if (!this.nim?.V2NIMMessageService?.revokeMessage) {
			throw new Error('云信消息服务尚未初始化')
		}
		const sourceMessage = msgInfo.sourceMessage || msgInfo
		if (!sourceMessage?.messageClientId && !sourceMessage?.messageServerId) {
			throw new Error('缺少云信消息对象，无法撤回')
		}
		await this.nim.V2NIMMessageService.revokeMessage(sourceMessage, params)
		const conversationType = Number(sourceMessage.conversationType || 0)
		const isGroup = conversationType === 2 || msgInfo.groupId || String(sourceMessage.conversationId || '').includes('team')
		const targetId = msgInfo.groupId || msgInfo.recvId || getTargetIdFromConversation(sourceMessage)
		return {
			id: `${msgInfo.id || sourceMessage.messageClientId || Date.now()}_recall`,
			sendId: this.currentAccount || msgInfo.sendId || sourceMessage.senderId || '',
			recvId: isGroup ? undefined : targetId,
			groupId: isGroup ? targetId : undefined,
			content: msgInfo.id || sourceMessage.messageServerId || sourceMessage.messageClientId || '',
			type: MESSAGE_TYPE.RECALL,
			status: MESSAGE_STATUS.DELIVERED,
			sendTime: Date.now(),
			selfSend: true,
			sourceMessage
		}
	}

	async setP2PMessageMute(accountId, mute) {
		if (!this.nim?.V2NIMSettingService) {
			throw new Error('云信设置服务尚未初始化')
		}
		return this.nim.V2NIMSettingService.setP2PMessageMuteMode(String(accountId), mute ? 1 : 0)
	}

	getP2PMessageMute(accountId) {
		if (!this.nim?.V2NIMSettingService?.getP2PMessageMuteMode) {
			return false
		}
		return this.nim.V2NIMSettingService.getP2PMessageMuteMode(String(accountId)) === 1
	}

	async setConversationTop(chatInfo = {}, stickTop) {
		if (!this.nim?.V2NIMConversationService) {
			throw new Error('云信云端会话服务尚未初始化')
		}
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		return this.nim.V2NIMConversationService.stickTopConversation(conversationId, !!stickTop)
	}

	async deleteCloudConversation(chatInfo = {}) {
		if (!this.nim?.V2NIMConversationService) {
			throw new Error('云信云端会话服务尚未初始化')
		}
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		if (!conversationId) {
			throw new Error('会话 ID 为空，无法删除云端会话')
		}
		const service = this.nim.V2NIMConversationService
		if (!service.deleteConversation) {
			throw new Error('当前云信 SDK 不支持删除云端会话')
		}
		await service.deleteConversation(conversationId)
		if (service.getConversationListByIds) {
			const conversations = await service.getConversationListByIds([conversationId]).catch(error => {
				console.warn('[YunxinIM] 校验云端会话删除结果失败:', conversationId, error)
				return []
			})
			if ((conversations || []).some(item => String(item?.conversationId) === String(conversationId))) {
				throw new Error('云端会话删除后仍存在')
			}
		}
		console.log('[YunxinIM] 删除云端会话成功:', conversationId)
	}

	async ensureCloudConversation(conversationId) {
		if (!conversationId) {
			throw new Error('会话 ID 为空，无法创建云端会话')
		}
		const service = this.nim?.V2NIMConversationService
		if (!service?.createConversation) {
			return
		}
		await service.createConversation(conversationId).catch(error => {
			const code = Number(error?.code || error?.detail?.code || 0)
			if (code !== 110405) {
				console.warn('[YunxinIM] 创建云端会话失败:', conversationId, error)
			}
		})
	}

	async clearConversationMessages(chatInfo = {}) {
		if (!this.nim?.V2NIMMessageService?.clearHistoryMessage) {
			throw new Error('云信消息服务尚未初始化')
		}
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		if (!conversationId) {
			throw new Error('会话 ID 为空，无法清空聊天记录')
		}
		return this.nim.V2NIMMessageService.clearHistoryMessage({
			conversationId,
			deleteRoam: true,
			onlineSync: true,
			clearMode: 0
		})
	}

	async setUserBlock(accountId, block) {
		if (!this.nim?.V2NIMUserService) {
			throw new Error('云信用户服务尚未初始化')
		}
		return block
			? this.nim.V2NIMUserService.addUserToBlockList(String(accountId))
			: this.nim.V2NIMUserService.removeUserFromBlockList(String(accountId))
	}

	async checkUserBlock(accountId) {
		if (!this.nim?.V2NIMUserService?.checkBlock) {
			return false
		}
		const result = await this.nim.V2NIMUserService.checkBlock([String(accountId)])
		return !!result?.[String(accountId)]
	}

	isPeerRead(message) {
		if (!message || !this.nim?.V2NIMMessageService?.isPeerRead) {
			return false
		}
		try {
			return !!this.nim.V2NIMMessageService.isPeerRead(message)
		} catch (error) {
			return false
		}
	}

	refreshPeerReadStatus(messages = []) {
		return messages.map(message => ({
			...message,
			status: message.selfSend && this.isPeerRead(message.sourceMessage) ? MESSAGE_STATUS.READED : message.status
		}))
	}

	async getFriendApplications(option = {}) {
		if (!this.nim?.V2NIMFriendService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		const result = await this.nim.V2NIMFriendService.getAddApplicationList({
			offset: 0,
			limit: 100,
			status: [0],
			...option
		})
		const applications = result?.infos || []
		const accountIds = applications.flatMap(item => [item.applicantAccountId, item.recipientAccountId])
		const profiles = await this.getUserProfiles(accountIds).catch(() => [])
		const profileMap = profiles.reduce((map, profile) => {
			map[profile.accountId] = profile
			return map
		}, {})
		return applications.map(item => normalizeSdkApplicationToLegacy(item, profileMap))
	}

	async addFriend(accountId, postscript = '', addMode = 2) {
		if (!this.nim?.V2NIMFriendService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		return this.nim.V2NIMFriendService.addFriend(String(accountId), {
			addMode,
			postscript
		})
	}

	async acceptFriendApplication(application) {
		if (!this.nim?.V2NIMFriendService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		return this.nim.V2NIMFriendService.acceptAddApplication(application.sourceApplication || application)
	}

	async rejectFriendApplication(application, postscript = '') {
		if (!this.nim?.V2NIMFriendService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		return this.nim.V2NIMFriendService.rejectAddApplication(application.sourceApplication || application, postscript)
	}

	async setFriendRemark(accountId, alias = '') {
		if (!this.nim?.V2NIMFriendService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		return this.nim.V2NIMFriendService.setFriendInfo(String(accountId), {
			alias
		})
	}

	async deleteFriendApplication(application) {
		if (!this.nim?.V2NIMFriendService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		return this.nim.V2NIMFriendService.deleteAddApplication(application.sourceApplication || application)
	}

	async sendTextMessage(conversationId, text, options = {}) {
		if (!this.nim?.V2NIMMessageCreator || !this.nim?.V2NIMMessageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		const message = await this.nim.V2NIMMessageCreator.createTextMessage(text || '')
		const sendOptions = applyOutgoingMessageExtension(message, options)
		const result = await this.nim.V2NIMMessageService.sendMessage(message, conversationId, sendOptions)
		return result?.message || result
	}

	async sendImageMessage(conversationId, fileObj, options = {}, onProgress = null, imageInfo = {}) {
		if (!this.nim?.V2NIMMessageCreator || !this.nim?.V2NIMMessageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		if (!fileObj) {
			throw new Error('图片文件不能为空')
		}
		const name = fileObj.name || `image_${Date.now()}`
		const width = Number(imageInfo.width || 0) || undefined
		const height = Number(imageInfo.height || 0) || undefined
		const message = await this.nim.V2NIMMessageCreator.createImageMessage(fileObj, name, 'nim_default_im', width, height)
		const sendOptions = applyOutgoingMessageExtension(message, options)
		const result = await this.nim.V2NIMMessageService.sendMessage(message, conversationId, sendOptions, onProgress || undefined)
		return result?.message || result
	}

	async sendVideoMessage(conversationId, fileObj, options = {}, onProgress = null, videoInfo = {}) {
		if (!this.nim?.V2NIMMessageCreator || !this.nim?.V2NIMMessageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		if (!fileObj) {
			throw new Error('视频文件不能为空')
		}
		const name = fileObj.name || `video_${Date.now()}`
		const duration = Number(videoInfo.duration || 0) || undefined
		const width = Number(videoInfo.width || 0) || undefined
		const height = Number(videoInfo.height || 0) || undefined
		const message = await this.nim.V2NIMMessageCreator.createVideoMessage(fileObj, name, 'nim_default_im', duration, width, height)
		const sendOptions = applyOutgoingMessageExtension(message, options)
		const result = await this.nim.V2NIMMessageService.sendMessage(message, conversationId, sendOptions, onProgress || undefined)
		return result?.message || result
	}

	async sendFileMessage(conversationId, fileObj, options = {}, onProgress = null) {
		if (!this.nim?.V2NIMMessageCreator || !this.nim?.V2NIMMessageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		if (!fileObj) {
			throw new Error('文件不能为空')
		}
		const name = fileObj.name || `file_${Date.now()}`
		const message = await this.nim.V2NIMMessageCreator.createFileMessage(fileObj, name, 'nim_default_im')
		const sendOptions = applyOutgoingMessageExtension(message, options)
		const result = await this.nim.V2NIMMessageService.sendMessage(message, conversationId, sendOptions, onProgress || undefined)
		return result?.message || result
	}

	async sendAudioMessage(conversationId, fileObj, options = {}, onProgress = null, audioInfo = {}) {
		if (!this.nim?.V2NIMMessageCreator || !this.nim?.V2NIMMessageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		if (!fileObj) {
			throw new Error('语音文件不能为空')
		}
		const name = fileObj.name || `audio_${Date.now()}.wav`
		const duration = Number(audioInfo.duration || 0) || undefined
		const message = await this.nim.V2NIMMessageCreator.createAudioMessage(fileObj, name, 'nim_default_im', duration)
		const sendOptions = applyOutgoingMessageExtension(message, options)
		const result = await this.nim.V2NIMMessageService.sendMessage(message, conversationId, sendOptions, onProgress || undefined)
		return result?.message || result
	}

	async sendCardMessage(conversationId, cardType, cardData = {}, options = {}) {
		if (!this.nim?.V2NIMMessageCreator || !this.nim?.V2NIMMessageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		const rawAttachment = JSON.stringify({
			type: cardType,
			data: cardData
		})
		const message = await this.nim.V2NIMMessageCreator.createCustomMessage('[名片]', rawAttachment)
		const sendOptions = applyOutgoingMessageExtension(message, options)
		const result = await this.nim.V2NIMMessageService.sendMessage(message, conversationId, sendOptions)
		return result?.message || result
	}

	async sendP2PCustomNotification(accountId, content, params = {}) {
		if (!this.nim?.V2NIMNotificationService) {
			throw new Error('云信通知服务尚未初始化')
		}
		const conversationId = this.getConversationId(accountId, 'p2p')
		if (!conversationId) {
			throw new Error('云信会话 ID 生成失败')
		}
		return this.nim.V2NIMNotificationService.sendCustomNotification(
			conversationId,
			typeof content === 'string' ? content : JSON.stringify(content),
			params
		)
	}

	async sendLegacyTextMessage(chatInfo = {}, text, options = {}) {
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		await this.ensureCloudConversation(conversationId)
		const message = await this.sendTextMessage(conversationId, text, options)
		const legacyMessage = await normalizeSdkMessageToLegacyAsync(message)
		return {
			...legacyMessage,
			selfSend: true,
			status: MESSAGE_STATUS.DELIVERED
		}
	}

	async sendLegacyImageMessage(chatInfo = {}, fileObj, options = {}, onProgress = null, imageInfo = {}) {
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		await this.ensureCloudConversation(conversationId)
		const message = await this.sendImageMessage(conversationId, fileObj, options, onProgress, imageInfo)
		const legacyMessage = normalizeSdkMessageToLegacy(message)
		return {
			...legacyMessage,
			selfSend: true,
			status: MESSAGE_STATUS.DELIVERED
		}
	}

	async sendLegacyVideoMessage(chatInfo = {}, fileObj, options = {}, onProgress = null, videoInfo = {}) {
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		await this.ensureCloudConversation(conversationId)
		const message = await this.sendVideoMessage(conversationId, fileObj, options, onProgress, videoInfo)
		const legacyMessage = normalizeSdkMessageToLegacy(message)
		return {
			...legacyMessage,
			selfSend: true,
			status: MESSAGE_STATUS.DELIVERED
		}
	}

	async sendLegacyFileMessage(chatInfo = {}, fileObj, options = {}, onProgress = null) {
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		await this.ensureCloudConversation(conversationId)
		const message = await this.sendFileMessage(conversationId, fileObj, options, onProgress)
		const legacyMessage = normalizeSdkMessageToLegacy(message)
		return {
			...legacyMessage,
			selfSend: true,
			status: MESSAGE_STATUS.DELIVERED
		}
	}

	async sendLegacyAudioMessage(chatInfo = {}, fileObj, options = {}, onProgress = null, audioInfo = {}) {
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		await this.ensureCloudConversation(conversationId)
		const message = await this.sendAudioMessage(conversationId, fileObj, options, onProgress, audioInfo)
		const legacyMessage = normalizeSdkMessageToLegacy(message)
		return {
			...legacyMessage,
			selfSend: true,
			status: MESSAGE_STATUS.DELIVERED
		}
	}

	async sendLegacyCardMessage(chatInfo = {}, cardType, cardData = {}, options = {}) {
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		await this.ensureCloudConversation(conversationId)
		const message = await this.sendCardMessage(conversationId, cardType, cardData, options)
		const legacyMessage = normalizeSdkMessageToLegacy(message)
		return {
			...legacyMessage,
			selfSend: true,
			status: MESSAGE_STATUS.DELIVERED
		}
	}

	async forwardLegacyMessage(chatInfo = {}, msgInfo = {}, options = {}) {
		if (!this.nim?.V2NIMMessageCreator || !this.nim?.V2NIMMessageService) {
			throw new Error('云信 SDK 尚未初始化')
		}
		const conversationId = chatInfo.conversationId || this.getConversationId(chatInfo.targetId, chatInfo.type === 'GROUP' ? 'team' : 'p2p')
		const sourceMessage = msgInfo.sourceMessage || msgInfo
		let message = null
		if (sourceMessage?.messageClientId || sourceMessage?.messageServerId) {
			message = this.nim.V2NIMMessageCreator.createForwardMessage(sourceMessage)
		}
		if (!message && msgInfo.type === MESSAGE_TYPE.TEXT) {
			message = await this.nim.V2NIMMessageCreator.createTextMessage(msgInfo.content || '')
		}
		if (!message && (msgInfo.type === MESSAGE_TYPE.USER_CARD || msgInfo.type === MESSAGE_TYPE.GROUP_CARD)) {
			message = await this.nim.V2NIMMessageCreator.createCustomMessage('[名片]', JSON.stringify({
				type: msgInfo.type,
				data: parseJson(msgInfo.content, {})
			}))
		}
		if (!message) {
			throw new Error('该消息缺少云信原始消息体，暂不支持转发')
		}
		const sendOptions = applyOutgoingMessageExtension(message, options)
		const result = await this.nim.V2NIMMessageService.sendMessage(message, conversationId, sendOptions)
		const legacyMessage = await normalizeSdkMessageToLegacyAsync(result?.message || result)
		return {
			...legacyMessage,
			selfSend: true,
			status: MESSAGE_STATUS.DELIVERED
		}
	}

	getConversationId(targetId, scene = 'p2p') {
		const util = this.nim?.V2NIMConversationIdUtil
		if (!util) {
			return ''
		}
		return scene === 'team' ? util.teamConversationId(String(targetId)) : util.p2pConversationId(String(targetId))
	}

	parseConversationTargetId(conversationId) {
		try {
			const targetId = this.nim?.V2NIMConversationIdUtil?.parseConversationTargetId?.(conversationId)
			if (targetId) {
				return targetId
			}
		} catch (error) {
			console.warn('[YunxinIM] 解析会话目标失败:', error)
		}
		const parts = String(conversationId || '').split('|')
		return parts[parts.length - 1] || ''
	}

	parseConversationType(conversationId) {
		try {
			const type = Number(this.nim?.V2NIMConversationIdUtil?.parseConversationType?.(conversationId) || 0)
			if (type) {
				return type
			}
		} catch (error) {
			console.warn('[YunxinIM] 解析会话类型失败:', error)
		}
		const parts = String(conversationId || '').split('|')
		return Number(parts[1] || 0)
	}
}

const yunxinIM = new YunxinIMService()

export { normalizeLoginInfo, normalizeSdkMessageToLegacy, normalizeSdkMessageToLegacyAsync, normalizeSdkApplicationToLegacy, normalizeSdkFriendToLegacy, normalizeSdkUserToLegacy }
export default yunxinIM
